import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import useUsers from "../../hooks/useUsers";
import ProfileContext from "./ProfileContext";
import { fromNow } from "../../helpers/common.helper";
import useWorks from "../../hooks/useWorks";
import AppContext from "../../AppContext";
import Rating from "../common/Rating";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as EmptySvg } from "../../assets/icons/empty-projects.svg";

const assetsUri = import.meta.env.ASSETS_URI;

const Reviews = (props) => {
  const { viewProject, viewProfile, viewCompany } = useContext(AppContext);
  const { user, isCompany, company } = useContext(ProfileContext);
  const { queryUserRatings, queryUsers, queryCompanies } = useUsers();
  const { queryWorks } = useWorks();
  const [reviews, setReviews] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    let _reviews = [];

    if (isCompany) {
      const { data: _works } = await queryWorks(
        {
          company: company._id,
        },
        null,
        -1,
        {
          _id: 1,
        }
      );

      const { data: reviews } = await queryUserRatings(
        {
          work: { $in: _works.map((w) => w._id) },
        },
        undefined,
        -1
      );

      _reviews = reviews;
    }

    if (!isCompany) {
      const { data: reviews } = await queryUserRatings(
        {
          for: user._id,
        },
        undefined,
        -1
      );

      _reviews = reviews;
    }

    // * Get Users
    const { data: _users } = await queryUsers(
      {
        _id: {
          $in: _reviews.map((r) => r.ratedBy),
        },
      },
      undefined,
      -1,
      {
        firstname: 1,
        surname: 1,
        avatar: 1,
      }
    );

    // * Get companies
    const { data: _companies } = await queryCompanies(
      {
        _id: {
          $in: _reviews.map((r) => r.ratedBy),
        },
      },
      undefined,
      -1,
      {
        logo: 1,
        name: 1,
      }
    );

    // * Get Works
    const { data: _works } = await queryWorks(
      {
        _id: {
          $in: _reviews.filter((r) => !!r.work).map((r) => r.work),
        },
      },
      undefined,
      -1,
      {
        banner: 1,
        type: 1,
      }
    );

    setReviews(
      _reviews.map((r) => {
        // * Map user to get name and avatar
        const _work = _works.find((w) => w._id === r.work);
        const _user = _users.find((u) => u._id === r.ratedBy);
        const _company = _companies.find((c) => c._id === r.ratedBy);

        if (!_user && !_company) {
          return {
            _id: r._id,
            user: r.ratedBy,
            userId: r.ratedBy,
            score: r.rate,
            review: r.msg,
            createdOn: new Date(r.updatedOn ?? r.createdOn),
            work: "",
          };
        }

        const name = _user
          ? `${_user.firstname} ${_user.surname ?? ""}`
          : _company.name;

        const avatar = _user ? _user.avatar : _company.logo;

        const review = {
          _id: r._id,
          avatar: avatar,
          user: name,
          reviewerId: _user?._id ?? _company._id,
          score: r.rate,
          review: r.msg,
          createdOn: new Date(r.updatedOn ?? r.createdOn),
          work: _work,
          isCompany: _company ? true : false,
        };

        return review;
      })
    );
  };

  return (
    <div className="profile-content-reviews">
      {reviews.length > 0 && (
        <div className="profile-content-reviews__title">Reviews by clients</div>
      )}

      {reviews.length === 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <EmptySvg height={224} style={{ opacity: 0.3 }} />
          <span style={{ fontSize: 16, color: "#9d9d9d", marginTop: -8 }}>
            No results found
          </span>
        </div>
      )}
      <div className="profile-content-reviews-list">
        {reviews.map((review, index) => {
          const {
            reviewerId,
            isCompany,
            avatar,
            score,
            user,
            createdOn,
            review: msg,
            work,
          } = review;

          return (
            <div
              className="profile-content-reviews-list-review"
              key={`review-${index}`}
            >
              <img
                className="profile-content-reviews-list-review__avatar"
                src={`${assetsUri}/${avatar}`}
                onError={({ target }) => {
                  target.src = avatar;
                }}
                alt="avatar"
                onClick={() => {
                  if (isCompany) {
                    viewCompany(reviewerId);
                    return;
                  }

                  viewProfile(reviewerId);
                }}
              />
              <div className="profile-content-reviews-list-review__name">
                {user}
              </div>
              <div className="profile-content-reviews-list-review__score">
                <Rating score={score} size={20} readOnly />
              </div>
              <div className="profile-content-reviews-list-review__date">
                {fromNow(createdOn)}
              </div>
              <div className="profile-content-reviews-list-review__message">
                {msg}
              </div>
              {work && (
                <div className="profile-content-reviews-list-review-work">
                  <img
                    className="profile-content-reviews-list-review-work__image"
                    onClick={() => {
                      if (work.type === 2) {
                        navigate(`/work-exchanges/${work._id}`);
                        return;
                      }

                      viewProject(work._id, location);
                    }}
                    alt="work"
                    src={`${assetsUri}/${work.banner}`}
                    onError={({ target }) => {
                      target.src = work.banner;
                    }}
                  />
                </div>
              )}
              <div className="profile-content-reviews-list-review__separator" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

Reviews.propTypes = {};

export default Reviews;
